import React, { useEffect, useState } from 'react';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { Robot } from '@phosphor-icons/react';
import client from '../../api/client';
import AppIcon from '../AppIcon';
import ConciergeTestChat from './ConciergeTestChat';

const TONES = [['warm', 'Warm'], ['formal', 'Formal'], ['playful', 'Playful'], ['concise', 'Concise']];
const SOURCE_LABELS = { venue: 'Venue & time', schedule: 'Schedule', travel: 'Travel & hotels', registry: 'Registry & funds', dress_code: 'Dress code', faq: 'Website FAQ' };
const inp = 'w-full px-3 py-2 rounded-lg border border-[#EFE3D8] text-sm bg-white focus:outline-none focus:border-[#7B2C3B]';

export const ConciergeScriptEditor = ({ toast }) => {
  const [s, setS] = useState(null);
  const [sources, setSources] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { client.get('/concierge/script').then(({ data }) => { setS(data.script); setSources(data.sources); }).catch(() => {}); }, []);
  if (!s) return null;
  const setFaq = (i, patch) => setS({ ...s, faqs: s.faqs.map((f, j) => (j === i ? { ...f, ...patch } : f)) });
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await client.put('/concierge/script', { ...s, faqs: s.faqs.filter((f) => f.q.trim()) });
      setS(data.script);
      toast?.({ title: 'Concierge updated', description: 'Your wedding line uses these answers right away.' });
    } catch (e) { toast?.({ title: 'Could not save', description: e.message, variant: 'destructive' }); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="concierge-script-editor" className="grid grid-cols-1 xl:grid-cols-[1.3fr_1fr] gap-5">
      <section className="soft-card bg-white rounded-2xl border border-[#EFE3D8] p-5 space-y-4">
        <div className="flex items-center gap-3">
          <AppIcon feature="ai" icon={Robot} size="md" reveal />
          <div>
            <h3 className="font-serif text-xl text-[#2A2A2A]">Concierge script</h3>
            <p className="text-xs text-[#6A6A6A]">What your wedding line says when guests call or text. Your AI and recording disclosures always play first.</p>
          </div>
        </div>
        <label className="block text-xs uppercase tracking-wide text-[#6A6A6A]">Greeting add-on
          <textarea data-testid="concierge-greeting" rows={2} value={s.greeting} onChange={(e) => setS({ ...s, greeting: e.target.value })} placeholder="e.g. We're so glad you're coming to celebrate with us!" className={`${inp} mt-1 normal-case`} />
        </label>
        <div>
          <p className="text-xs uppercase tracking-wide text-[#6A6A6A] mb-1.5">Tone</p>
          <div className="flex flex-wrap gap-2">
            {TONES.map(([v, l]) => (
              <button key={v} data-testid={`concierge-tone-${v}`} onClick={() => setS({ ...s, tone: v })} className={`px-4 py-1.5 rounded-full text-xs border transition-colors ${s.tone === v ? 'bg-[#7B2C3B] text-white border-[#7B2C3B]' : 'bg-white border-[#EFE3D8] text-[#6A6A6A] hover:border-[#B8896C]'}`}>{l}</button>
            ))}
          </div>
        </div>
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-wide text-[#6A6A6A]">Answers to common questions</p>
          {s.faqs.map((f, i) => (
            <div key={f.key} data-testid={`concierge-faq-${f.key}`} className="p-3 rounded-xl bg-[#FBF7F2] border border-[#F2E8DE] space-y-1.5">
              <div className="flex gap-2">
                <input data-testid={`concierge-faq-q-${f.key}`} value={f.q} onChange={(e) => setFaq(i, { q: e.target.value })} className={`${inp} font-medium`} />
                <button aria-label="Remove question" onClick={() => setS({ ...s, faqs: s.faqs.filter((_, j) => j !== i) })} className="text-[#B8896C] hover:text-[#7B2C3B]"><Trash2 className="w-4 h-4" /></button>
              </div>
              <textarea data-testid={`concierge-faq-a-${f.key}`} rows={2} value={f.a} onChange={(e) => setFaq(i, { a: e.target.value })} placeholder="Your answer (leave blank to skip)" className={inp} />
            </div>
          ))}
          <button data-testid="concierge-faq-add" onClick={() => setS({ ...s, faqs: [...s.faqs, { key: `custom_${Date.now().toString(36)}`, q: '', a: '' }] })} className="text-xs px-3 py-1.5 rounded-full border border-[#EFE3D8] hover:bg-[#F6EAE0] inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Add a question</button>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wide text-[#6A6A6A] mb-1.5">Wedding details the Concierge can use</p>
          <div className="grid grid-cols-2 gap-2">
            {sources.map((k) => (
              <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
                <input data-testid={`concierge-source-${k}`} type="checkbox" className="accent-[#7B2C3B]" checked={s.sources[k] !== false} onChange={(e) => setS({ ...s, sources: { ...s.sources, [k]: e.target.checked } })} /> {SOURCE_LABELS[k] || k}
              </label>
            ))}
          </div>
        </div>
        <button data-testid="concierge-script-save" onClick={save} disabled={busy} className="btn-primary text-sm px-5 py-2 rounded-full inline-flex items-center gap-1.5 disabled:opacity-50">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Save script
        </button>
      </section>
      <ConciergeTestChat />
    </div>
  );
};

export default ConciergeScriptEditor;
